/**
 * The field manual's navigation — PURE (no DOM, no keybinds import): pagination, page turns, search with snippets and
 * the back-of-book index. The module hands in the key-text function so this file stays testable in node
 * (verify:help-book).
 */
import type { HelpBook, HelpChapter, HelpPage, HelpPart } from './types.js';

/** A page in reading order with its folio (1-based page number) and its place in the book. */
export interface FlatPage {
  page: HelpPage;
  chapter: HelpChapter;
  part: HelpPart;
  /** 1-based chapter number across the book. */
  chapterNo: number;
  /** 1-based page number (the folio). */
  folio: number;
  /** Lower-cased plain text of title + body + terms, for search. */
  plain: string;
  /** The body as plain text in its own case (for snippets). */
  bodyText: string;
}

export type KeyText = (action: string) => string | null;

/** Replace `{{key:action}}` with the binding (HTML `<kbd>` or plain text). An unknown action stays visible. */
export function fillKeys(text: string, keyText: KeyText, html: boolean): string {
  return text.replace(/\{\{key:([A-Za-z]+)\}\}/g, (m, a: string) => {
    const k = keyText(a);
    if (k === null) return m;
    const safe = k.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    return html ? `<kbd>${safe}</kbd>` : k;
  });
}

/** HTML → plain text: tags out, the common entities decoded, whitespace collapsed. */
export function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?)])/g, '$1') // a tag before punctuation left a stray space ("codes , ten")
    .trim();
}

/** Every page in reading order, numbered. */
export function flatten(book: HelpBook, keyText: KeyText): FlatPage[] {
  const out: FlatPage[] = [];
  let chapterNo = 0;
  for (const part of book) {
    for (const chapter of part.chapters) {
      chapterNo++;
      for (const page of chapter.pages) {
        const bodyText = plainText(fillKeys(page.body, keyText, false));
        const plain = `${page.title} ${bodyText} ${(page.terms ?? []).join(' ')}`.toLowerCase();
        out.push({ page, chapter, part, chapterNo, folio: out.length + 1, plain, bodyText });
      }
    }
  }
  return out;
}

/** Index of a page id in the flat list, or −1. */
export function indexOfPage(flat: readonly FlatPage[], id: string): number {
  for (let i = 0; i < flat.length; i++) if (flat[i].page.id === id) return i;
  return -1;
}

/** The first page of each chapter (for the contents page): chapter id → folio. */
export function chapterFolios(flat: readonly FlatPage[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const f of flat) if (!m.has(f.chapter.id)) m.set(f.chapter.id, f.folio);
  return m;
}

// ── Search ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface SearchHit {
  index: number;
  score: number;
  /** Plain snippet around the first match; `<mark>` is added by the renderer over `[start, end)` ranges. */
  snippet: string;
  /** Ranges of query words inside `snippet`. */
  marks: Array<[number, number]>;
}

/** Split a query into lower-cased words (2+ characters, or a single digit / symbol word like "ⓒ"). */
export function queryWords(q: string): string[] {
  return q.toLowerCase().split(/[^\p{L}\p{N}ⓒ+]+/u).filter((w) => w.length >= 2 || /^[\p{N}ⓒ]$/u.test(w));
}

/**
 * Rank pages for a query. Every word must appear (AND); a title hit outranks a body hit; a whole-word hit outranks a
 * prefix; earlier body hits rank slightly higher. Ties keep reading order.
 */
export function search(flat: readonly FlatPage[], q: string, limit = 60): SearchHit[] {
  const words = queryWords(q);
  if (!words.length) return [];
  const hits: SearchHit[] = [];
  for (let i = 0; i < flat.length; i++) {
    const f = flat[i];
    const title = f.page.title.toLowerCase();
    let score = 0;
    let ok = true;
    for (const w of words) {
      const at = f.plain.indexOf(w);
      if (at < 0) { ok = false; break; }
      if (title.includes(w)) score += 10;
      if (new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(w)}([^\\p{L}\\p{N}]|$)`, 'u').test(f.plain)) score += 3;
      score += 1 + Math.max(0, 1 - at / 4000);
      if ((f.page.terms ?? []).some((t) => t.toLowerCase().includes(w))) score += 4;
    }
    if (!ok) continue;
    const { snippet, marks } = makeSnippet(f, words);
    hits.push({ index: i, score, snippet, marks });
  }
  hits.sort((a, b) => b.score - a.score || a.index - b.index);
  return hits.slice(0, limit);
}

function escapeRe(s: string): string { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/** ~180 characters of the page around the first body match, with the matched words' ranges. */
export function makeSnippet(f: FlatPage, words: readonly string[], width = 180): { snippet: string; marks: Array<[number, number]> } {
  // The BODY (not the title) so the snippet says something new.
  const body = f.bodyText;
  const lower = body.toLowerCase();
  let first = -1;
  for (const w of words) { const at = lower.indexOf(w); if (at >= 0 && (first < 0 || at < first)) first = at; }
  let start = Math.max(0, (first < 0 ? 0 : first) - Math.floor(width / 3));
  if (start > 0) { const sp = body.indexOf(' ', start); if (sp > 0 && sp - start < 20) start = sp + 1; }
  let end = Math.min(body.length, start + width);
  if (end < body.length) { const sp = body.lastIndexOf(' ', end); if (sp > start + width / 2) end = sp; }
  const snippet = (start > 0 ? '… ' : '') + body.slice(start, end) + (end < body.length ? ' …' : '');
  const marks: Array<[number, number]> = [];
  const sl = snippet.toLowerCase();
  for (const w of words) {
    let at = sl.indexOf(w);
    while (at >= 0 && marks.length < 24) { marks.push([at, at + w.length]); at = sl.indexOf(w, at + w.length); }
  }
  marks.sort((a, b) => a[0] - b[0]);
  // merge overlaps
  const merged: Array<[number, number]> = [];
  for (const m of marks) {
    const last = merged[merged.length - 1];
    if (last && m[0] <= last[1]) last[1] = Math.max(last[1], m[1]); else merged.push([m[0], m[1]]);
  }
  return { snippet, marks: merged };
}

// ── The index ──────────────────────────────────────────────────────────────────────────────────────────────────────

export interface IndexEntry {
  term: string;
  /** Folios, ascending, unique. */
  folios: number[];
}

/**
 * The back-of-book index: every `<b>term</b>` the pages define (the bold words ARE the book's key terms) plus each
 * page's `terms`, mapped to the folios that carry them. Terms are case-folded for merging and shown as first written.
 */
export function buildIndex(flat: readonly FlatPage[], keyText: KeyText): IndexEntry[] {
  const byKey = new Map<string, { term: string; folios: Set<number> }>();
  const add = (raw: string, folio: number): void => {
    const term = plainText(raw).replace(/^[\s:,.;—–-]+|[\s:,.;—–-]+$/g, '');
    if (term.length < 3 || term.length > 40) return;
    if (/^[\d\s.,%×+−-]+$/.test(term)) return; // a bare number is not an index entry
    const key = term.toLowerCase();
    let e = byKey.get(key);
    if (!e) { e = { term: term[0].toUpperCase() + term.slice(1), folios: new Set() }; byKey.set(key, e); }
    e.folios.add(folio);
  };
  for (const f of flat) {
    const html = fillKeys(f.page.body, keyText, false);
    for (const m of html.matchAll(/<b>([^<]{2,60})<\/b>/g)) add(m[1], f.folio);
    for (const t of f.page.terms ?? []) add(t, f.folio);
    add(f.page.title, f.folio);
  }
  // One entry per word: a plural folds into its singular when both appear ("Sandstorms" → "Sandstorm").
  for (const [key, e] of [...byKey]) {
    if (key.length < 5 || !key.endsWith('s')) continue;
    const one = byKey.get(key.slice(0, -1));
    if (!one) continue;
    for (const f of e.folios) one.folios.add(f);
    byKey.delete(key);
  }
  return [...byKey.values()]
    .map((e) => ({ term: e.term, folios: [...e.folios].sort((a, b) => a - b) }))
    .sort((a, b) => a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }));
}

/** The index's first letters (A–Z, then '#' for anything else) → whether any term starts there. */
export function indexLetters(entries: readonly IndexEntry[]): Map<string, boolean> {
  const m = new Map<string, boolean>();
  for (const c of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#') m.set(c, false);
  for (const e of entries) m.set(letterOf(e.term), true);
  return m;
}

export function letterOf(term: string): string {
  const c = term.normalize('NFD')[0]?.toUpperCase() ?? '#';
  return c >= 'A' && c <= 'Z' ? c : '#';
}

// ── Cross-references ──────────────────────────────────────────────────────────────────────────────────────────────

/** Every page id a page points at (its `see` list and its inline `data-page` links). */
export function pageRefs(page: HelpPage): string[] {
  const out = new Set<string>(page.see ?? []);
  for (const m of page.body.matchAll(/data-page="([^"]+)"/g)) out.add(m[1]);
  return [...out];
}

// ── Bookmarks & the last page (persisted by the module; the shape lives here so the harness can check it) ──────

export interface BookState { last: string | null; marks: string[] }

/** Revive a stored state, dropping anything malformed or no longer in the book. Never throws. */
export function reviveState(raw: string | null, ids: ReadonlySet<string>): BookState {
  const empty: BookState = { last: null, marks: [] };
  if (!raw) return empty;
  try {
    const v = JSON.parse(raw) as Partial<BookState>;
    const last = typeof v.last === 'string' && ids.has(v.last) ? v.last : null;
    const marks = Array.isArray(v.marks) ? v.marks.filter((m): m is string => typeof m === 'string' && ids.has(m)).slice(0, 64) : [];
    return { last, marks: [...new Set(marks)] };
  } catch { return empty; }
}
